import React from 'react';
import { type ServerUiComponent } from '@features/server-ui';

import { type SemanticMetricTilePayload } from '../../types';
import { isMetricGated, MetricTile } from '../MetricTile';
import { useOptionalOptimization } from '../OptimizationModalProvider';

import { optimizationContextFromWire, tileFromWire } from './tileAdapter';

/**
 * SemanticMetricTile — the SDUI domain component for the `semantic_metric_tile`
 * domain_payload. Reads the wire contract (get_metric_tile) and renders the
 * shared presentational MetricTile. One component, every metric.
 *
 * When the backend marks the metric an `opportunity` (lever present · clears the
 * gate · below target · not just intervened) AND a modal host is mounted, the
 * lever becomes an "Improve this" CTA that opens the white-glove funnel.
 */
export const SemanticMetricTile: React.FC<{ component: ServerUiComponent }> = ({
  component,
}) => {
  const optimization = useOptionalOptimization();
  const data = component.data as unknown as SemanticMetricTilePayload | undefined;
  if (!data) return null;
  const tile = tileFromWire(data);
  // Gated metrics are never drawn as cards (the metrics page lists their names
  // in the ghost tile instead) — suppress defensively if the container still
  // hands us a below-gate tile.
  if (isMetricGated(tile)) return null;

  const opportunity = optimization ? optimizationContextFromWire(data) : null;
  const onImprove =
    optimization && opportunity
      ? () => optimization.openOptimization(opportunity)
      : undefined;

  return <MetricTile data={tile} onImprove={onImprove} />;
};
